import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Text, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ChevronLeft20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import type { Weekday } from "@gymplan/shared";
import { WEEKDAYS } from "@gymplan/shared";

/** A block on the calendar. `weekday` + times (HH:MM) — template blocks repeat weekly. */
export interface CalBlock {
  id: string;
  weekday: Weekday;
  startTime: string;
  endTime: string;
  title?: string;
  subtitle?: string;
  kind?: "availability" | "session" | "preference";
  /** Non-editable blocks (e.g. sessions overlaid on the availability editor) */
  readOnly?: boolean;
}

export interface WeekCalendarProps {
  blocks: CalBlock[];
  /** Monday of the displayed week */
  weekStart: Date;
  onWeekChange?: (monday: Date) => void;
  editable?: boolean;
  onCreate?: (b: { weekday: Weekday; startTime: string; endTime: string }) => void;
  onMove?: (id: string, b: { weekday: Weekday; startTime: string; endTime: string }) => void;
  onSelect?: (id: string, anchor: HTMLElement) => void;
  selectedId?: string | null;
  /** Default duration (minutes) for a click-without-drag create */
  defaultDurationMinutes?: number;
  startHour?: number;
  endHour?: number;
  /** Header slot (right side) */
  toolbar?: React.ReactNode;
  /** Hide week navigation (for templates where dates don't matter) */
  showNav?: boolean;
}

const ROW_MIN = 30; // minutes per visual row
const ROW_PX = 28;
const SNAP = 15; // minutes

export const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const toHHMM = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const snap = (m: number) => Math.round(m / SNAP) * SNAP;

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "8px", userSelect: "none" },
  header: { display: "flex", alignItems: "center", gap: "8px" },
  title: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, minWidth: "180px" },
  scroller: { overflow: "auto", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground1, maxHeight: "70vh" },
  grid: { display: "grid", gridTemplateColumns: "56px repeat(7, minmax(110px, 1fr))", minWidth: "840px" },
  dayHead: { position: "sticky", top: 0, zIndex: 3, backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, padding: "8px 6px", textAlign: "center" },
  dayHeadToday: { color: tokens.colorBrandForeground1 },
  dayNum: { display: "inline-grid", placeItems: "center", width: "26px", height: "26px", borderRadius: "50%", fontWeight: tokens.fontWeightSemibold },
  dayNumToday: { backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand },
  timeCol: { position: "relative", borderRight: `1px solid ${tokens.colorNeutralStroke2}` },
  timeLabel: { position: "absolute", right: "6px", transform: "translateY(-50%)", fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  dayCol: { position: "relative", borderRight: `1px solid ${tokens.colorNeutralStroke3}`, backgroundImage: `repeating-linear-gradient(to bottom, ${tokens.colorNeutralStroke3} 0 1px, transparent 1px ${ROW_PX * 2}px), repeating-linear-gradient(to bottom, transparent 0 ${ROW_PX}px, ${tokens.colorNeutralStroke3} ${ROW_PX}px ${ROW_PX + 1}px, transparent ${ROW_PX + 1}px ${ROW_PX * 2}px)` },
  dayColEditable: { cursor: "crosshair" },
  dayColToday: { backgroundColor: tokens.colorBrandBackground2 },
  block: { position: "absolute", left: "2px", right: "4px", borderRadius: tokens.borderRadiusMedium, padding: "3px 6px", overflow: "hidden", fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, boxSizing: "border-box", boxShadow: tokens.shadow2, zIndex: 1 },
  availability: { backgroundColor: tokens.colorBrandBackground2, border: `1px solid ${tokens.colorBrandStroke2}`, color: tokens.colorBrandForeground2, cursor: "grab" },
  session: { backgroundColor: tokens.colorPaletteGreenBackground2, border: `1px solid ${tokens.colorPaletteGreenBorder1}`, color: tokens.colorPaletteGreenForeground2, zIndex: 2 },
  preference: { backgroundColor: tokens.colorPaletteMarigoldBackground2, border: `1px dashed ${tokens.colorPaletteMarigoldBorder1}`, color: tokens.colorPaletteMarigoldForeground2, opacity: 0.9 },
  selected: { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: "1px", zIndex: 4 },
  dragging: { opacity: 0.7, cursor: "grabbing" },
  ghost: { backgroundColor: tokens.colorBrandBackgroundInvertedSelected, border: `1px dashed ${tokens.colorBrandStroke1}`, pointerEvents: "none", zIndex: 5 },
  resize: { position: "absolute", left: 0, right: 0, bottom: 0, height: "6px", cursor: "ns-resize" },
  nowLine: { position: "absolute", left: 0, right: 0, height: "2px", backgroundColor: tokens.colorPaletteRedBorder2, zIndex: 3, pointerEvents: "none" },
});

type Drag =
  | { mode: "create"; weekday: Weekday; anchorMin: number; curMin: number }
  | { mode: "move"; id: string; grabOffsetMin: number; duration: number; weekday: Weekday; startMin: number; moved: boolean }
  | { mode: "resize"; id: string; weekday: Weekday; startMin: number; curEndMin: number };

export function WeekCalendar({
  blocks,
  weekStart,
  onWeekChange,
  editable = false,
  onCreate,
  onMove,
  onSelect,
  selectedId,
  defaultDurationMinutes = 60,
  startHour = 5,
  endHour = 22,
  toolbar,
  showNav = true,
}: WeekCalendarProps) {
  const s = useStyles();
  const gridRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const dayStartMin = startHour * 60;
  const dayEndMin = endHour * 60;
  const totalPx = ((dayEndMin - dayStartMin) / ROW_MIN) * ROW_PX;
  const pxPerMin = ROW_PX / ROW_MIN;
  const yToMin = (y: number) => Math.max(dayStartMin, Math.min(dayEndMin, snap(dayStartMin + y / pxPerMin)));
  const days = useMemo(() => WEEKDAYS.map((wd, i) => ({ wd, date: addDays(weekStart, i) })), [weekStart]);

  // ----- pointer handling (one listener set on the grid) -----
  const pointerPos = useCallback((e: React.PointerEvent, col: HTMLElement) => {
    const r = col.getBoundingClientRect();
    return yToMin(e.clientY - r.top);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startHour, endHour]);

  const onColPointerDown = (wd: Weekday) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!editable || e.button !== 0) return;
    if ((e.target as HTMLElement).dataset.block) return; // handled by block
    const m = pointerPos(e, e.currentTarget);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ mode: "create", weekday: wd, anchorMin: m, curMin: m });
  };

  const onBlockPointerDown = (b: CalBlock) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!editable || b.readOnly) {
      onSelect?.(b.id, e.currentTarget);
      return;
    }
    const col = e.currentTarget.parentElement as HTMLElement;
    const m = pointerPos(e, col);
    const start = toMin(b.startTime);
    const isResize = (e.target as HTMLElement).dataset.resize === "1";
    col.setPointerCapture(e.pointerId);
    if (isResize) {
      setDrag({ mode: "resize", id: b.id, weekday: b.weekday, startMin: start, curEndMin: toMin(b.endTime) });
    } else {
      setDrag({ mode: "move", id: b.id, grabOffsetMin: m - start, duration: toMin(b.endTime) - start, weekday: b.weekday, startMin: start, moved: false });
    }
  };

  const onColPointerMove = (wd: Weekday) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    // Which column is the pointer over? (capture keeps events on the origin column)
    const grid = gridRef.current!;
    const cols = Array.from(grid.querySelectorAll<HTMLElement>("[data-day]"));
    const over = cols.find((c) => {
      const r = c.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX < r.right;
    });
    const overWd = (over?.dataset.day as Weekday | undefined) ?? wd;
    const m = pointerPos(e, e.currentTarget);
    if (drag.mode === "create") setDrag({ ...drag, curMin: m });
    else if (drag.mode === "move") {
      const start = Math.max(dayStartMin, Math.min(dayEndMin - drag.duration, snap(m - drag.grabOffsetMin)));
      setDrag({ ...drag, weekday: overWd, startMin: start, moved: drag.moved || start !== toMin(blocks.find((b) => b.id === drag.id)?.startTime ?? "00:00") || overWd !== drag.weekday });
    } else if (drag.mode === "resize") setDrag({ ...drag, curEndMin: Math.max(drag.startMin + SNAP, m) });
  };

  const onColPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (drag.mode === "create") {
      let a = Math.min(drag.anchorMin, drag.curMin);
      let b = Math.max(drag.anchorMin, drag.curMin);
      if (b - a < SNAP) b = Math.min(dayEndMin, a + defaultDurationMinutes); // plain click
      if (b > a) onCreate?.({ weekday: drag.weekday, startTime: toHHMM(a), endTime: toHHMM(b) });
    } else if (drag.mode === "move") {
      if (drag.moved) onMove?.(drag.id, { weekday: drag.weekday, startTime: toHHMM(drag.startMin), endTime: toHHMM(drag.startMin + drag.duration) });
      else onSelect?.(drag.id, e.target as HTMLElement);
    } else if (drag.mode === "resize") {
      onMove?.(drag.id, { weekday: drag.weekday, startTime: toHHMM(drag.startMin), endTime: toHHMM(drag.curEndMin) });
    }
    setDrag(null);
  };

  // ----- render helpers -----
  const blockStyle = (startMin: number, endMin: number): React.CSSProperties => ({
    top: (startMin - dayStartMin) * pxPerMin,
    height: Math.max(ROW_PX / 2, (endMin - startMin) * pxPerMin - 2),
  });

  const visibleBlock = (b: CalBlock) => {
    if (drag?.mode === "move" && drag.id === b.id) return { ...b, weekday: drag.weekday, startTime: toHHMM(drag.startMin), endTime: toHHMM(drag.startMin + drag.duration) };
    if (drag?.mode === "resize" && drag.id === b.id) return { ...b, endTime: toHHMM(drag.curEndMin) };
    return b;
  };

  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
  const weekLabel = `${format(weekStart, "d MMM")} – ${format(addDays(weekStart, 6), "d MMM yyyy")}`;
  const nowMin = now.getHours() * 60 + now.getMinutes();

  return (
    <div className={s.root}>
      {(showNav || toolbar) && (
        <div className={s.header}>
          {showNav && (
            <>
              <Button appearance="subtle" icon={<ChevronLeft20Regular />} aria-label="Previous week" onClick={() => onWeekChange?.(addDays(weekStart, -7))} />
              <Button appearance="subtle" icon={<ChevronRight20Regular />} aria-label="Next week" onClick={() => onWeekChange?.(addDays(weekStart, 7))} />
              <Button appearance="secondary" size="small" onClick={() => onWeekChange?.(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Today
              </Button>
              <Text className={s.title}>{weekLabel}</Text>
            </>
          )}
          <div style={{ flex: 1 }} />
          {toolbar}
        </div>
      )}

      <div className={s.scroller}>
        <div className={s.grid} ref={gridRef}>
          {/* header row */}
          <div className={s.dayHead} />
          {days.map(({ wd, date }) => {
            const today = isSameDay(date, now);
            return (
              <div key={wd} className={mergeClasses(s.dayHead, today && s.dayHeadToday)}>
                <Text size={200} block weight={showNav ? "regular" : "semibold"}>
                  {format(date, showNav ? "EEE" : "EEEE")}
                </Text>
                {showNav && <span className={mergeClasses(s.dayNum, today && s.dayNumToday)}>{format(date, "d")}</span>}
              </div>
            );
          })}

          {/* time column */}
          <div className={s.timeCol} style={{ height: totalPx }}>
            {hours.filter((h) => h > startHour).map((h) => (
              <span key={h} className={s.timeLabel} style={{ top: (h - startHour) * 60 * pxPerMin }}>
                {String(h).padStart(2, "0")}:00
              </span>
            ))}
          </div>

          {/* day columns */}
          {days.map(({ wd, date }) => {
            const today = isSameDay(date, now);
            const dayBlocks = blocks.map(visibleBlock).filter((b) => b.weekday === wd);
            return (
              <div
                key={wd}
                data-day={wd}
                className={mergeClasses(s.dayCol, editable && s.dayColEditable, today && s.dayColToday)}
                style={{ height: totalPx }}
                onPointerDown={onColPointerDown(wd)}
                onPointerMove={onColPointerMove(wd)}
                onPointerUp={onColPointerUp}
                onPointerCancel={() => setDrag(null)}
              >
                {today && nowMin >= dayStartMin && nowMin <= dayEndMin && <div className={s.nowLine} style={{ top: (nowMin - dayStartMin) * pxPerMin }} />}
                {dayBlocks.map((b) => {
                  const kind = b.kind ?? "availability";
                  const isDragging = drag && "id" in drag && drag.id === b.id;
                  const inner = (
                    <div
                      key={b.id}
                      data-block="1"
                      className={mergeClasses(s.block, s[kind], selectedId === b.id && s.selected, !!isDragging && s.dragging)}
                      style={{ ...blockStyle(toMin(b.startTime), toMin(b.endTime)), cursor: editable && !b.readOnly ? undefined : "pointer", ...(editable && b.readOnly ? { pointerEvents: "none" as const, zIndex: 0 } : {}) }}
                      onPointerDown={onBlockPointerDown(b)}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {b.startTime}–{b.endTime}
                      </div>
                      {b.title && <div style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{b.title}</div>}
                      {b.subtitle && <div style={{ opacity: 0.8, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{b.subtitle}</div>}
                      {editable && !b.readOnly && <div data-block="1" data-resize="1" className={s.resize} />}
                    </div>
                  );
                  return b.subtitle ? (
                    <Tooltip key={b.id} content={`${b.title ?? ""} ${b.subtitle}`.trim()} relationship="description" withArrow>
                      {inner}
                    </Tooltip>
                  ) : (
                    inner
                  );
                })}
                {drag?.mode === "create" && drag.weekday === wd && (
                  <div className={mergeClasses(s.block, s.ghost)} style={blockStyle(Math.min(drag.anchorMin, drag.curMin), Math.max(drag.anchorMin, drag.curMin) || Math.min(drag.anchorMin, drag.curMin) + SNAP)}>
                    {toHHMM(Math.min(drag.anchorMin, drag.curMin))}–{toHHMM(Math.max(drag.anchorMin, drag.curMin))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
